// ============================================================
// CONTENT MASTER — Fuente única de contenido para Admin Radio
// Todas las imágenes, videos, lives, posts y eventos comparten
// IDs canónicos y referencias cruzadas.
// ============================================================

// ─── Tipos Canónicos ───────────────────────────────────────

export type CanonicalContentType = 'photo' | 'video' | 'live' | 'post' | 'event';

export type CanonicalContentStatus =
  | 'published'
  | 'draft'
  | 'scheduled'
  | 'ended'
  | 'live'
  | 'cancelled';

// ─── Tipos Canónicos ───────────────────────────────────────

export type CanonicalLiveStatus =
  | 'draft'
  | 'scheduled'
  | 'live'
  | 'ended'
  | 'recording_available'
  | 'published'
  | 'archived';

export interface CanonicalEngagement {
  likes: number;
  comments: number;
  shares: number;
  reach: number;
}

export interface CanonicalPhoto {
  id: string; // "photo-001" ... "photo-024"
  title: string;
  imageUrl: string;
  albumId: string; // "album-001" ... "album-006"
  albumName: string;
  category: 'cabina' | 'eventos' | 'locutores' | 'comunidad' | 'promociones';
  isFeatured: boolean;
  fileSize: string;
  dimensions: string;
  createdAt: string;
}

export interface CanonicalAlbum {
  id: string; // "album-001" ...
  name: string;
  coverPhotoId: string;
  photoCount: number;
  category: string;
  createdAt: string;
}

export interface CanonicalVideo {
  id: string; // "video-001" ...
  title: string;
  description: string;
  category: 'programas' | 'entrevistas' | 'eventos' | 'shorts' | 'promociones';
  thumbnailPhotoId: string; // referencia a canonical photo
  sourceType: 'upload' | 'youtube' | 'facebook';
  sourceUrl: string;
  duration: string;
  status: 'published' | 'draft' | 'scheduled';
  isFeatured: boolean;
  fileSize: string;
  createdAt: string;
  scheduledAt?: string;
  // Live → Video lineage
  derivedFromLiveId?: string;
}

export interface CanonicalRecording {
  id: string; // "rec-001" ...
  liveId: string;
  title: string;
  description: string;
  hostDisplayName: string;
  programName?: string;
  date: string;
  duration: string; // mock duration
  thumbnailPhotoId: string;
  platform: string;
  audiencePeak: number;
  audienceAvg: number;
  totalViews: number;
  status: 'processing' | 'available';
  createdAt: string;
}

export interface CanonicalLive {
  id: string; // "live-001" ...
  title: string;
  description: string;
  date: string;
  time: string;
  hostPersonId: string; // referencia a persons.ts
  hostDisplayName: string;
  thumbnailPhotoId: string;
  provider: 'youtube' | 'facebook' | 'vps' | 'mux';
  providerUrl: string;
  status: CanonicalLiveStatus;
  createdAt: string;
  // Post-live lineage
  generatedVideoId?: string; // video generado al finalizar
  generatedPostId?: string; // post generado al finalizar
  generatedRecordingId?: string; // recording generado al finalizar
}

export interface CanonicalPost {
  id: string; // "post-001" ...
  title: string;
  text: string;
  postType: 'text' | 'image' | 'video' | 'poll' | 'sponsored';
  imagePhotoId?: string; // referencia a canonical photo
  videoId?: string; // referencia a canonical video
  scheduledAt?: string;
  status: 'published' | 'draft' | 'scheduled';
  isFeatured: boolean;
  engagement: CanonicalEngagement;
  pollData?: {
    pollType: 'single' | 'multiple';
    isClosed: boolean;
    options: { id: number; text: string; votes: number }[];
  };
  isSponsored: boolean;
  sponsorName?: string;
  createdAt: string;
  updatedAt: string;
  // Lineage
  derivedFromVideoId?: string;
  derivedFromLiveId?: string;
  referencesEventId?: string;
  referencesProgramId?: string;
}

export interface CanonicalEvent {
  id: string; // "event-001" ...
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  eventType: 'concert' | 'meet_greet' | 'special_broadcast' | 'autograph' | 'acoustic_session' | 'launch';
  status: 'draft' | 'published' | 'ended' | 'cancelled';
  modality: 'presencial' | 'online' | 'hibrido';
  capacity: number;
  category: 'music' | 'sports' | 'community' | 'cultural' | 'religious' | 'family' | 'educational';
  imagePhotoId: string; // referencia a canonical photo
  highlighted: boolean;
  participants: number;
  interested: number;
  shares: number;
  followers: number;
  stationId: number;
  // Cross-references
  relatedPostIds: string[];
  relatedVideoIds: string[];
  relatedLiveIds: string[];
}

// ─── Catálogo de Fotos (fuente única de imágenes) ──────────

export const canonicalAlbums: CanonicalAlbum[] = [
  { id: 'album-001', name: 'Detrás de Cámaras 2026', coverPhotoId: 'photo-001', photoCount: 18, category: 'cabina', createdAt: '2026-06-01T10:00:00' },
  { id: 'album-002', name: 'Festival Parque Central', coverPhotoId: 'photo-005', photoCount: 32, category: 'eventos', createdAt: '2026-05-14T16:30:00' },
  { id: 'album-003', name: 'Nuestros Locutores', coverPhotoId: 'photo-009', photoCount: 12, category: 'locutores', createdAt: '2026-04-20T09:00:00' },
  { id: 'album-004', name: 'Meet & Greet Oyentes', coverPhotoId: 'photo-012', photoCount: 24, category: 'comunidad', createdAt: '2026-03-08T14:15:00' },
  { id: 'album-005', name: 'Summer Fest Promos', coverPhotoId: 'photo-015', photoCount: 15, category: 'promociones', createdAt: '2026-05-25T11:00:00' },
  { id: 'album-006', name: 'Concierto Acústico - Los Ecos', coverPhotoId: 'photo-017', photoCount: 21, category: 'eventos', createdAt: '2026-06-03T20:00:00' },
];

export const canonicalPhotos: CanonicalPhoto[] = [
  // ── Álbum 1: Detrás de Cámaras ──
  { id: 'photo-001', title: 'Carlos al aire - Tardes de Pop', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20host%20speaking%20into%20professional%20broadcast%20microphone%20in%20studio%2C%20warm%20ambient%20lighting%2C%20focused%20expression%2C%20headphones%20around%20neck%2C%20modern%20radio%20booth%20equipment%20visible%2C%20editorial%20documentary%20style&width=800&height=600&seq=photo-carlos-aire&orientation=landscape', albumId: 'album-001', albumName: 'Detrás de Cámaras 2026', category: 'cabina', isFeatured: true, fileSize: '3.2 MB', dimensions: '2400 x 1600', createdAt: '2026-06-07T15:30:00' },
  { id: 'photo-002', title: 'Consola de mezcla en acción', imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20audio%20mixing%20console%20with%20illuminated%20buttons%20and%20faders%2C%20radio%20broadcast%20equipment%20close-up%2C%20warm%20studio%20lighting%2C%20technical%20production%20environment%2C%20hands%20adjusting%20levels%20on%20soundboard&width=800&height=600&seq=photo-consola&orientation=landscape', albumId: 'album-001', albumName: 'Detrás de Cámaras 2026', category: 'cabina', isFeatured: false, fileSize: '4.1 MB', dimensions: '2400 x 1600', createdAt: '2026-06-07T14:45:00' },
  { id: 'photo-003', title: 'Preparando playlist del día', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20producer%20selecting%20music%20tracks%20on%20computer%20screen%2C%20studio%20environment%20with%20monitors%20and%20audio%20equipment%2C%20focused%20work%20atmosphere%2C%20warm%20professional%20lighting%2C%20candid%20behind%20the%20scenes%20moment&width=800&height=600&seq=photo-playlist&orientation=landscape', albumId: 'album-001', albumName: 'Detrás de Cámaras 2026', category: 'cabina', isFeatured: false, fileSize: '2.8 MB', dimensions: '2000 x 1500', createdAt: '2026-06-06T10:15:00' },
  { id: 'photo-004', title: 'Equipo antes del show nocturno', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20production%20team%20group%20photo%20in%20studio%20before%20evening%20broadcast%2C%20four%20people%20smiling%2C%20casual%20attire%2C%20warm%20ambient%20studio%20lighting%2C%20behind%20the%20scenes%20camaraderie%2C%20documentary%20style&width=800&height=600&seq=photo-equipo-nocturno&orientation=landscape', albumId: 'album-001', albumName: 'Detrás de Cámaras 2026', category: 'cabina', isFeatured: false, fileSize: '5.3 MB', dimensions: '3000 x 2000', createdAt: '2026-06-05T21:00:00' },
  // ── Álbum 2: Festival Parque Central ──
  { id: 'photo-005', title: 'Escenario principal - Festival', imageUrl: 'https://readdy.ai/api/search-image?query=Main%20stage%20at%20outdoor%20music%20festival%20with%20spectacular%20lighting%20rig%2C%20large%20LED%20screens%2C%20crowd%20anticipation%20before%20show%2C%20dramatic%20sunset%20sky%20in%20background%2C%20professional%20event%20photography%20wide%20shot&width=800&height=600&seq=photo-escenario-fest&orientation=landscape', albumId: 'album-002', albumName: 'Festival Parque Central', category: 'eventos', isFeatured: true, fileSize: '6.7 MB', dimensions: '3000 x 2000', createdAt: '2026-05-14T17:00:00' },
  { id: 'photo-006', title: 'Multitud vibrando con la música', imageUrl: 'https://readdy.ai/api/search-image?query=Excited%20festival%20crowd%20with%20hands%20raised%20enjoying%20live%20music%2C%20colorful%20stage%20lights%20illuminating%20audience%2C%20energetic%20concert%20atmosphere%2C%20wide%20angle%20crowd%20shot%2C%20summer%20outdoor%20music%20event&width=800&height=600&seq=photo-crowd-fest&orientation=landscape', albumId: 'album-002', albumName: 'Festival Parque Central', category: 'eventos', isFeatured: false, fileSize: '7.1 MB', dimensions: '3200 x 2133', createdAt: '2026-05-14T18:30:00' },
  { id: 'photo-007', title: 'Food trucks en el festival', imageUrl: 'https://readdy.ai/api/search-image?query=Colorful%20food%20trucks%20lined%20up%20at%20outdoor%20music%20festival%2C%20people%20queuing%20for%20food%2C%20string%20lights%20overhead%2C%20festive%20atmosphere%2C%20summer%20evening%20vibes%2C%20lifestyle%20event%20photography&width=800&height=600&seq=photo-foodtrucks&orientation=landscape', albumId: 'album-002', albumName: 'Festival Parque Central', category: 'eventos', isFeatured: false, fileSize: '5.4 MB', dimensions: '2800 x 1867', createdAt: '2026-05-14T16:00:00' },
  { id: 'photo-008', title: 'Banda Los Ecos en el escenario', imageUrl: 'https://readdy.ai/api/search-image?query=Rock%20band%20performing%20energetically%20on%20festival%20stage%2C%20lead%20singer%20with%20microphone%2C%20guitarists%20in%20action%2C%20dramatic%20purple%20and%20amber%20stage%20lighting%2C%20smoke%20effects%2C%20concert%20action%20photography&width=800&height=600&seq=photo-band-ecos&orientation=landscape', albumId: 'album-002', albumName: 'Festival Parque Central', category: 'eventos', isFeatured: false, fileSize: '6.3 MB', dimensions: '3000 x 2000', createdAt: '2026-05-14T20:00:00' },
  // ── Álbum 3: Nuestros Locutores ──
  { id: 'photo-009', title: 'Natalia - La voz de las mañanas', imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20female%20radio%20host%20in%20studio%2C%20warm%20confident%20smile%2C%20headphones%20around%20neck%2C%20soft%20studio%20lighting%2C%20friendly%20approachable%20expression%2C%20clean%20background%2C%20editorial%20portrait%20photography&width=800&height=600&seq=photo-natalia&orientation=portrait', albumId: 'album-003', albumName: 'Nuestros Locutores', category: 'locutores', isFeatured: true, fileSize: '2.9 MB', dimensions: '1600 x 2400', createdAt: '2026-04-22T11:00:00' },
  { id: 'photo-010', title: 'Diego - Noches de Radio', imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20male%20radio%20host%20with%20headphones%2C%20evening%20broadcast%20atmosphere%2C%20warm%20dim%20studio%20lighting%2C%20thoughtful%20expression%2C%20casual%20professional%20style%2C%20editorial%20portrait&width=800&height=600&seq=photo-diego&orientation=portrait', albumId: 'album-003', albumName: 'Nuestros Locutores', category: 'locutores', isFeatured: false, fileSize: '3.1 MB', dimensions: '1600 x 2400', createdAt: '2026-04-21T15:00:00' },
  { id: 'photo-011', title: 'Mariana - Fines de semana', imageUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20young%20female%20radio%20personality%2C%20energetic%20expression%2C%20casual%20trendy%20style%2C%20studio%20background%20with%20audio%20equipment%2C%20bright%20natural%20lighting%2C%20modern%20editorial%20portrait%20photography&width=800&height=600&seq=photo-mariana&orientation=portrait', albumId: 'album-003', albumName: 'Nuestros Locutores', category: 'locutores', isFeatured: false, fileSize: '3.4 MB', dimensions: '1600 x 2400', createdAt: '2026-04-20T10:30:00' },
  // ── Álbum 4: Meet & Greet Oyentes ──
  { id: 'photo-012', title: 'Grupo de oyentes en el estudio', imageUrl: 'https://readdy.ai/api/search-image?query=Group%20of%20happy%20radio%20listeners%20visiting%20broadcast%20studio%2C%20meeting%20hosts%2C%20group%20photo%20with%20radio%20station%20logo%20in%20background%2C%20warm%20friendly%20atmosphere%2C%20documentary%20style%20community%20event%20photography&width=800&height=600&seq=photo-oyentes-estudio&orientation=landscape', albumId: 'album-004', albumName: 'Meet & Greet Oyentes', category: 'comunidad', isFeatured: false, fileSize: '5.8 MB', dimensions: '2800 x 1867', createdAt: '2026-03-08T15:00:00' },
  { id: 'photo-013', title: 'Firma de autógrafos', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20hosts%20signing%20autographs%20for%20fans%20at%20community%20event%2C%20outdoor%20setting%20with%20banners%2C%20people%20queuing%20happily%2C%20warm%20afternoon%20light%2C%20community%20engagement%20photography%2C%20candid%20moments&width=800&height=600&seq=photo-autografos&orientation=landscape', albumId: 'album-004', albumName: 'Meet & Greet Oyentes', category: 'comunidad', isFeatured: false, fileSize: '4.9 MB', dimensions: '2600 x 1733', createdAt: '2026-03-08T16:30:00' },
  { id: 'photo-014', title: 'Foto grupal con la comunidad', imageUrl: 'https://readdy.ai/api/search-image?query=Large%20group%20photo%20of%20radio%20staff%20and%20community%20members%20together%2C%20outdoor%20sunny%20day%2C%20everyone%20smiling%20and%20waving%2C%20radio%20station%20banner%20visible%2C%20community%20celebration%20atmosphere%2C%20wide%20angle%20shot&width=800&height=600&seq=photo-grupal-comunidad&orientation=landscape', albumId: 'album-004', albumName: 'Meet & Greet Oyentes', category: 'comunidad', isFeatured: true, fileSize: '7.8 MB', dimensions: '3200 x 2133', createdAt: '2026-03-08T18:00:00' },
  // ── Álbum 5: Summer Fest Promos ──
  { id: 'photo-015', title: 'Stand promocional Summer Fest', imageUrl: 'https://readdy.ai/api/search-image?query=Colorful%20promotional%20booth%20at%20outdoor%20event%2C%20radio%20station%20branded%20merchandise%20display%2C%20staff%20handing%20out%20flyers%20and%20giveaways%2C%20bright%20summer%20day%2C%20festival%20promotional%20setup%20photography&width=800&height=600&seq=photo-stand-promo&orientation=landscape', albumId: 'album-005', albumName: 'Summer Fest Promos', category: 'promociones', isFeatured: false, fileSize: '5.2 MB', dimensions: '2700 x 1800', createdAt: '2026-05-26T13:00:00' },
  { id: 'photo-016', title: 'Sorteo de entradas en vivo', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20host%20holding%20ticket%20giveaway%20drawing%20on%20air%2C%20excited%20expression%2C%20colorful%20prize%20envelopes%2C%20studio%20setting%20with%20branded%20backdrop%2C%20promotional%20contest%20moment%2C%20engaging%20broadcast%20photography&width=800&height=600&seq=photo-sorteo&orientation=landscape', albumId: 'album-005', albumName: 'Summer Fest Promos', category: 'promociones', isFeatured: false, fileSize: '3.6 MB', dimensions: '2200 x 1650', createdAt: '2026-05-28T11:30:00' },
  // ── Álbum 6: Concierto Acústico ──
  { id: 'photo-017', title: 'Acústico íntimo en el estudio', imageUrl: 'https://readdy.ai/api/search-image?query=Intimate%20acoustic%20music%20performance%20in%20small%20radio%20studio%2C%20singer%20with%20acoustic%20guitar%2C%20warm%20candle-style%20lighting%2C%20close-up%20artistic%20shot%2C%20raw%20authentic%20music%20moment%2C%20moody%20documentary%20photography&width=800&height=600&seq=photo-acustico-intimo&orientation=landscape', albumId: 'album-006', albumName: 'Concierto Acústico - Los Ecos', category: 'eventos', isFeatured: true, fileSize: '4.5 MB', dimensions: '2500 x 1875', createdAt: '2026-06-03T20:45:00' },
  { id: 'photo-018', title: 'El vocalista en primer plano', imageUrl: 'https://readdy.ai/api/search-image?query=Close-up%20portrait%20of%20male%20singer%20performing%20passionately%2C%20microphone%20in%20hand%2C%20dramatic%20single%20spotlight%20lighting%2C%20raw%20emotion%20captured%2C%20black%20and%20white%20artistic%20concert%20photography%2C%20intense%20atmosphere&width=800&height=600&seq=photo-vocalista-close&orientation=portrait', albumId: 'album-006', albumName: 'Concierto Acústico - Los Ecos', category: 'eventos', isFeatured: false, fileSize: '3.8 MB', dimensions: '1800 x 2700', createdAt: '2026-06-03T21:10:00' },
  { id: 'photo-019', title: 'El equipo técnico del acústico', imageUrl: 'https://readdy.ai/api/search-image?query=Sound%20engineers%20working%20at%20mixing%20desk%20during%20acoustic%20live%20session%2C%20focused%20professionals%20adjusting%20audio%20levels%2C%20dim%20studio%20lighting%20with%20equipment%20LEDs%2C%20technical%20production%20environment%2C%20documentary%20style&width=800&height=600&seq=photo-tecnico-acustico&orientation=landscape', albumId: 'album-006', albumName: 'Concierto Acústico - Los Ecos', category: 'eventos', isFeatured: false, fileSize: '4.2 MB', dimensions: '2400 x 1600', createdAt: '2026-06-03T20:30:00' },
  { id: 'photo-020', title: 'Foto final con la banda', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20hosts%20posing%20together%20with%20visiting%20band%20members%20after%20acoustic%20session%2C%20group%20selfie%20style%2C%20warm%20studio%20background%2C%20everyone%20smiling%20and%20making%20fun%20gestures%2C%20candid%20celebration%20moment&width=800&height=600&seq=photo-final-banda&orientation=landscape', albumId: 'album-006', albumName: 'Concierto Acústico - Los Ecos', category: 'eventos', isFeatured: false, fileSize: '5.1 MB', dimensions: '2600 x 1950', createdAt: '2026-06-03T22:00:00' },
  // ── Extras cabina ──
  { id: 'photo-021', title: 'Micrófono vintage de colección', imageUrl: 'https://readdy.ai/api/search-image?query=Vintage%20classic%20broadcast%20microphone%20on%20stand%2C%20warm%20golden%20lighting%2C%20shallow%20depth%20of%20field%2C%20elegant%20studio%20setting%2C%20retro%20radio%20aesthetic%2C%20artistic%20product%20photography%2C%20nostalgic%20broadcasting%20equipment&width=800&height=600&seq=photo-mic-vintage&orientation=landscape', albumId: 'album-001', albumName: 'Detrás de Cámaras 2026', category: 'cabina', isFeatured: false, fileSize: '2.5 MB', dimensions: '2000 x 1500', createdAt: '2026-06-04T09:30:00' },
  { id: 'photo-022', title: 'Amanecer desde la cabina', imageUrl: 'https://readdy.ai/api/search-image?query=Sunrise%20view%20through%20radio%20studio%20window%2C%20silhouette%20of%20broadcast%20equipment%20against%20golden%20morning%20light%2C%20peaceful%20early%20morning%20atmosphere%2C%20warm%20orange%20and%20amber%20sky%2C%20contemplative%20mood%2C%20artistic%20documentary%20photography&width=800&height=600&seq=photo-amanecer&orientation=landscape', albumId: 'album-001', albumName: 'Detrás de Cámaras 2026', category: 'cabina', isFeatured: false, fileSize: '3.9 MB', dimensions: '2400 x 1600', createdAt: '2026-06-02T06:15:00' },
  // ── Extras promociones ──
  { id: 'photo-023', title: 'Merchandising oficial de la radio', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20branded%20merchandise%20display%2C%20t-shirts%20caps%20and%20mugs%20with%20station%20logo%2C%20flat%20lay%20product%20photography%2C%20clean%20bright%20lighting%2C%20promotional%20items%20arranged%20aesthetically%20on%20wooden%20surface&width=800&height=600&seq=photo-merch&orientation=landscape', albumId: 'album-005', albumName: 'Summer Fest Promos', category: 'promociones', isFeatured: false, fileSize: '3.3 MB', dimensions: '2200 x 1650', createdAt: '2026-05-30T14:00:00' },
  // ── Extras comunidad ──
  { id: 'photo-024', title: 'Niños visitando la radio', imageUrl: 'https://readdy.ai/api/search-image?query=School%20children%20visiting%20radio%20station%20on%20field%20trip%2C%20kids%20wearing%20headphones%20at%20broadcast%20console%2C%20educational%20tour%2C%20curious%20expressions%2C%20bright%20studio%20lighting%2C%20heartwarming%20community%20outreach%20photography&width=800&height=600&seq=photo-ninos-radio&orientation=landscape', albumId: 'album-004', albumName: 'Meet & Greet Oyentes', category: 'comunidad', isFeatured: false, fileSize: '5.6 MB', dimensions: '2800 x 1867', createdAt: '2026-03-08T10:00:00' },
];

// ─── Fotos auxiliares (no en álbumes, usadas por posts/lives) ──

// Posts usan muchas imágenes que no están en el catálogo de álbumes.
// Generamos IDs virtuales "photo-ext-001" a "photo-ext-020" para esas imágenes,
// manteniéndolas en el motor unificado para referencia cruzada.
export const canonicalExternalPhotos: { id: string; imageUrl: string; usedBy: string }[] = [
  { id: 'photo-ext-001', imageUrl: 'https://readdy.ai/api/search-image?query=Exciting%20music%20festival%20crowd%20with%20colorful%20stage%20lights%2C%20vibrant%20celebration%20atmosphere%2C%20wide%20angle%20outdoor%20concert%20view%2C%20energetic%20crowd%20hands%20up%2C%20warm%20golden%20hour%20lighting%2C%20editorial%20concert%20photography&width=800&height=450&seq=post-fb-concurso-festival-01&orientation=landscape', usedBy: 'post-001' },
  { id: 'photo-ext-002', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20studio%20broadcast%20booth%20with%20professional%20microphone%20and%20mixing%20console%2C%20warm%20ambient%20golden%20lighting%2C%20modern%20radio%20production%20setup%2C%20dynamic%20live%20transmission%20atmosphere%2C%20behind%20the%20scenes%20radio&width=800&height=450&seq=post-fb-live-tardes-02&orientation=landscape', usedBy: 'post-002' },
  { id: 'photo-ext-003', imageUrl: 'https://readdy.ai/api/search-image?query=Outdoor%20concert%20live%20performance%20at%20park%20venue%2C%20crowd%20enjoying%20music%20under%20dramatic%20stage%20lights%2C%20vibrant%20festival%20atmosphere%2C%20professional%20concert%20photography%2C%20wide%20shot%20with%20sea%20of%20people&width=800&height=450&seq=post-fb-concierto-parque-04&orientation=landscape', usedBy: 'post-004' },
  { id: 'photo-ext-004', imageUrl: 'https://readdy.ai/api/search-image?query=Summer%20music%20festival%20promotional%20design%2C%20outdoor%20event%20with%20live%20bands%20performing%2C%20colorful%20vibrant%20atmosphere%2C%20festival%20banners%20and%20crowd%2C%20warm%20sunny%20day%2C%20festive%20decorations%20and%20food%20trucks&width=800&height=450&seq=post-fb-festival-junio-05&orientation=landscape', usedBy: 'post-005' },
  { id: 'photo-ext-005', imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20radio%20programming%20schedule%20design%2C%20summer%20themed%20with%20warm%20tones%2C%20broadcast%20schedule%20layout%20with%20time%20slots%2C%20professional%20radio%20branding%2C%20contemporary%20graphic%20design%20with%20music%20elements&width=800&height=450&seq=post-fb-programacion-06&orientation=landscape', usedBy: 'post-006' },
  { id: 'photo-ext-006', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20community%20meet%20and%20greet%20event%2C%20group%20photos%20with%20hosts%20and%20listeners%2C%20casual%20friendly%20gathering%2C%20warm%20atmosphere%2C%20candid%20photography%2C%20people%20smiling%20and%20hugging%2C%20community%20radio%20vibe&width=800&height=450&seq=post-fb-meet-greet-08&orientation=landscape', usedBy: 'post-008' },
  { id: 'photo-ext-007', imageUrl: 'https://readdy.ai/api/search-image?query=New%20Year%20celebration%20broadcast%20from%20radio%20studio%2C%20festive%20decorations%20and%20warm%20lighting%2C%20champagne%20toast%20moment%2C%20celebration%20atmosphere%2C%20party%20mood%2C%20countdown%20on%20screen%2C%20radio%20hosts%20celebrating&width=800&height=450&seq=post-fb-nye-10&orientation=landscape', usedBy: 'post-010' },
  { id: 'photo-ext-008', imageUrl: 'https://readdy.ai/api/search-image?query=Summer%20music%20festival%20ticket%20giveaway%20promotional%20image%2C%20bright%20beach%20festival%20vibes%2C%20colorful%20concert%20wristbands%20and%20golden%20tickets%2C%20energetic%20tropical%20design%2C%20palm%20trees%20and%20stage%20lights&width=800&height=450&seq=post-fb-summerfest-11&orientation=landscape', usedBy: 'post-011' },
  { id: 'photo-ext-009', imageUrl: 'https://readdy.ai/api/search-image?query=Vintage%20vinyl%20record%20player%20with%20classic%20Beatles%20album%20spinning%2C%20warm%20retro%20atmosphere%2C%20analog%20music%20nostalgia%2C%20classic%20rock%20radio%20show%20promotion%2C%20moody%20warm%20amber%20lighting%2C%20nostalgic%20aesthetic&width=800&height=450&seq=post-fb-vinilo-beatles-12&orientation=landscape', usedBy: 'post-012' },
  { id: 'photo-ext-010', imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20responsive%20website%20design%20displayed%20on%20laptop%20and%20mobile%20devices%2C%20clean%20interface%20with%20radio%20player%20widget%2C%20bright%20professional%20web%20design%20showcase%2C%20contemporary%20digital%20experience%2C%20warm%20inviting%20colors&width=800&height=450&seq=post-fb-web-nueva-13&orientation=landscape', usedBy: 'post-013' },
  { id: 'photo-ext-011', imageUrl: 'https://readdy.ai/api/search-image?query=Behind%20the%20scenes%20at%20radio%20station%2C%20crew%20preparing%20equipment%20for%20broadcast%2C%20candid%20photography%20with%20warm%20studio%20lighting%2C%20professional%20broadcast%20environment%2C%20authentic%20work%20atmosphere%2C%20team%20collaboration&width=800&height=450&seq=post-fb-backstage-14&orientation=landscape', usedBy: 'post-014' },
  { id: 'photo-ext-012', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20merchandise%20store%20display%2C%20t-shirts%20caps%20and%20mugs%20with%20radio%20branding%2C%20clean%20product%20photography%20on%20wooden%20surface%2C%20warm%20studio%20lighting%2C%20lifestyle%20product%20shot%2C%20trendy%20merchandise%20layout&width=800&height=450&seq=post-fb-tienda-promo-16&orientation=landscape', usedBy: 'post-016' },
  { id: 'photo-ext-013', imageUrl: 'https://readdy.ai/api/search-image?query=Logo%20design%20contest%20creative%20brief%2C%20graphic%20design%20workspace%20with%20sketches%20and%20color%20palettes%2C%20modern%20brand%20identity%20concept%2C%20creative%20professional%20environment%2C%20design%20tools%20and%20moodboard%20on%20desk&width=800&height=450&seq=post-fb-concurso-logo-18&orientation=landscape', usedBy: 'post-018' },
  { id: 'photo-ext-014', imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20awards%20voting%20promotion%2C%20golden%20trophy%20award%20with%20radio%20microphone%2C%20elegant%20ceremony%20atmosphere%2C%20dramatic%20spotlight%2C%20prestigious%20award%20show%20vibe%2C%20golden%20and%20warm%20color%20palette&width=800&height=450&seq=post-fb-premios-radio-20&orientation=landscape', usedBy: 'post-020' },
];

// ─── Lives Canónicos ───────────────────────────────────────

export const canonicalLives: CanonicalLive[] = [
  {
    id: 'live-001', title: 'Tardes de Pop - En Vivo desde Cabina',
    description: 'Transmisión en vivo del programa Tardes de Pop. Música, entrevistas, solicitudes de los oyentes y la mejor energía de la tarde. Carlos Mendoza te acompaña con los mejores éxitos.',
    date: '2026-06-08', time: '15:00', hostPersonId: 'p-001', hostDisplayName: 'Carlos Mendoza',
    thumbnailPhotoId: 'photo-001',
    provider: 'youtube', providerUrl: 'https://youtube.com/live/example1', status: 'live', createdAt: '2026-06-08T14:30:00',
  },
  {
    id: 'live-002', title: 'Entrevista Exclusiva: Los Ecos en Vivo',
    description: 'La banda revelación del año nos visita para una entrevista íntima y un mini set acústico en vivo.',
    date: '2026-06-12', time: '18:00', hostPersonId: 'p-001', hostDisplayName: 'Carlos Mendoza',
    thumbnailPhotoId: 'photo-017',
    provider: 'facebook', providerUrl: 'https://facebook.com/live/example1', status: 'scheduled', createdAt: '2026-06-06T10:00:00',
  },
  {
    id: 'live-003', title: 'Fines de Semana - Maratón Electrónica',
    description: 'Mariana te trae 4 horas de la mejor música electrónica en vivo.',
    date: '2026-06-14', time: '20:00', hostPersonId: 'p-007', hostDisplayName: 'Mariana Vega',
    thumbnailPhotoId: 'photo-011',
    provider: 'youtube', providerUrl: 'https://youtube.com/live/example2', status: 'scheduled', createdAt: '2026-06-07T09:00:00',
  },
  {
    id: 'live-004', title: 'Noches de Radio - Especial Rock Nacional',
    description: 'Diego conduce una noche dedicada al rock nacional.',
    date: '2026-06-10', time: '22:00', hostPersonId: 'p-005', hostDisplayName: 'Diego Ramírez',
    thumbnailPhotoId: 'photo-010',
    provider: 'vps', providerUrl: '', status: 'scheduled', createdAt: '2026-06-05T16:00:00',
  },
  {
    id: 'live-005', title: 'Cobertura Festival Summer Fest 2026',
    description: 'Transmisión en vivo desde el Parque Central. Entrevistas con artistas, backstage exclusivo.',
    date: '2026-06-28', time: '12:00', hostPersonId: 'p-001', hostDisplayName: 'Carlos Mendoza + Natalia Ruiz',
    thumbnailPhotoId: 'photo-005',
    provider: 'youtube', providerUrl: 'https://youtube.com/live/example3', status: 'scheduled', createdAt: '2026-06-01T08:00:00',
    generatedVideoId: 'video-003',
  },
  {
    id: 'live-006', title: 'Mañanas con Natalia - Edición Especial',
    description: 'Natalia arranca la mañana con buena onda, noticias, el clima y la mejor playlist.',
    date: '2026-06-03', time: '07:00', hostPersonId: 'p-006', hostDisplayName: 'Natalia Ruiz',
    thumbnailPhotoId: 'photo-009',
    provider: 'facebook', providerUrl: 'https://facebook.com/live/example2', status: 'ended', createdAt: '2026-06-02T20:00:00',
    generatedRecordingId: 'rec-001',
  },
  {
    id: 'live-007', title: 'Concierto Acústico Íntimo - Los Ecos',
    description: 'Transmisión exclusiva del concierto acústico de Los Ecos desde nuestro estudio. 12 canciones en versiones nunca antes escuchadas.',
    date: '2026-06-04', time: '19:30', hostPersonId: 'p-001', hostDisplayName: 'Carlos Mendoza',
    thumbnailPhotoId: 'photo-017',
    provider: 'youtube', providerUrl: 'https://youtube.com/live/example4', status: 'ended', createdAt: '2026-06-03T12:00:00',
    generatedVideoId: 'video-014', generatedPostId: 'post-004',
    generatedRecordingId: 'rec-002',
  },
  {
    id: 'live-008', title: 'Debate: El Futuro de la Radio Digital',
    description: 'Mesa redonda en vivo con directores de radios digitales de Latinoamérica.',
    date: '2026-06-18', time: '16:00', hostPersonId: 'p-001', hostDisplayName: 'Carlos Mendoza',
    thumbnailPhotoId: 'photo-004',
    provider: 'mux', providerUrl: '', status: 'scheduled', createdAt: '2026-06-05T14:00:00',
  },
  {
    id: 'live-009', title: 'Programa Sorpresa - Anuncio Especial',
    description: '¡Gran anuncio en vivo! Algo grande se viene para RadioFrecuenciaWeb.',
    date: '2026-06-20', time: '12:00', hostPersonId: 'p-001', hostDisplayName: 'Todo el equipo',
    thumbnailPhotoId: 'photo-022',
    provider: 'youtube', providerUrl: 'https://youtube.com/live/example5', status: 'scheduled', createdAt: '2026-06-08T11:00:00',
  },
  {
    id: 'live-010', title: 'Meet & Greet Virtual con Oyentes',
    description: 'Transmisión especial donde los oyentes se conectan por videollamada.',
    date: '2026-05-30', time: '17:00', hostPersonId: 'p-006', hostDisplayName: 'Natalia Ruiz + Diego Ramírez',
    thumbnailPhotoId: 'photo-014',
    provider: 'facebook', providerUrl: 'https://facebook.com/live/example3', status: 'ended', createdAt: '2026-05-28T15:00:00',
    generatedVideoId: 'video-009',
    generatedRecordingId: 'rec-003',
  },
  {
    id: 'live-011', title: 'DJ Set en Vivo - After Hours',
    description: 'Sesión de música electrónica en vivo desde la cabina.',
    date: '2026-06-15', time: '23:00', hostPersonId: 'p-005', hostDisplayName: 'Diego Ramírez',
    thumbnailPhotoId: 'photo-010',
    provider: 'vps', providerUrl: '', status: 'scheduled', createdAt: '2026-06-07T22:00:00',
  },
  {
    id: 'live-012', title: 'Especial Navideño - Brindis con la Comunidad',
    description: 'El último live del año. Balance, agradecimientos, saludos de los oyentes y el brindis en vivo.',
    date: '2025-12-24', time: '20:00', hostPersonId: 'p-001', hostDisplayName: 'Todo el equipo',
    thumbnailPhotoId: 'photo-014',
    provider: 'youtube', providerUrl: 'https://youtube.com/live/example6', status: 'ended', createdAt: '2025-12-20T10:00:00',
    generatedRecordingId: 'rec-004',
  },
];

// ─── Videos Canónicos ──────────────────────────────────────

export const canonicalVideos: CanonicalVideo[] = [
  {
    id: 'video-001', title: 'Tardes de Pop - Programa completo 07/06',
    description: 'Edición completa del programa Tardes de Pop con Carlos Mendoza. Entrevista exclusiva a Los Ecos, estreno de nuevos temas.',
    category: 'programas', thumbnailPhotoId: 'photo-001', sourceType: 'upload', sourceUrl: '',
    duration: '1:58:00', status: 'published', isFeatured: true, fileSize: '2.8 GB', createdAt: '2026-06-07T20:30:00',
  },
  {
    id: 'video-002', title: 'Entrevista: Los Ecos - Nuevo álbum 2026',
    description: 'Charlamos con la banda Los Ecos sobre su nuevo álbum, su proceso creativo y la gira nacional.',
    category: 'entrevistas', thumbnailPhotoId: 'photo-008', sourceType: 'youtube', sourceUrl: 'https://youtube.com/watch?v=example1',
    duration: '45:22', status: 'published', isFeatured: true, fileSize: '1.2 GB', createdAt: '2026-06-06T16:00:00',
  },
  {
    id: 'video-003', title: 'Festival Parque Central - Resumen Oficial',
    description: 'Reviví los mejores momentos de nuestro festival anual. Actuaciones completas, entrevistas con los artistas.',
    category: 'eventos', thumbnailPhotoId: 'photo-005', sourceType: 'upload', sourceUrl: '',
    duration: '2:35:00', status: 'published', isFeatured: true, fileSize: '4.5 GB', createdAt: '2026-05-16T12:00:00',
    derivedFromLiveId: 'live-005',
  },
  {
    id: 'video-004', title: 'Shorts: Mejor llamada al aire del mes',
    description: 'La llamada más graciosa que recibimos en Tardes de Pop.',
    category: 'shorts', thumbnailPhotoId: 'photo-001', sourceType: 'facebook', sourceUrl: 'https://facebook.com/watch?v=example1',
    duration: '0:58', status: 'published', isFeatured: false, fileSize: '156 MB', createdAt: '2026-06-05T10:15:00',
  },
  {
    id: 'video-005', title: 'Promo: Summer Fest 2026 - ¡Vení a vivirlo!',
    description: 'Spot promocional del Summer Fest 2026. Música, food trucks, sorteos y los mejores artistas en vivo.',
    category: 'promociones', thumbnailPhotoId: 'photo-015', sourceType: 'upload', sourceUrl: '',
    duration: '1:30', status: 'published', isFeatured: false, fileSize: '380 MB', createdAt: '2026-06-04T14:00:00',
  },
  {
    id: 'video-006', title: 'Noches de Radio - Especial Beatles',
    description: 'Programa completo dedicado a la discografía de The Beatles.',
    category: 'programas', thumbnailPhotoId: 'photo-010', sourceType: 'upload', sourceUrl: '',
    duration: '2:12:00', status: 'published', isFeatured: false, fileSize: '3.1 GB', createdAt: '2026-06-03T22:00:00',
  },
  {
    id: 'video-007', title: 'Entrevista: Natalia Ramírez - 10 años al aire',
    description: 'Natalia repasa su primera década como locutora de RadioFrecuenciaWeb.',
    category: 'entrevistas', thumbnailPhotoId: 'photo-009', sourceType: 'youtube', sourceUrl: 'https://youtube.com/watch?v=example2',
    duration: '1:05:00', status: 'published', isFeatured: false, fileSize: '1.8 GB', createdAt: '2026-06-02T18:30:00',
  },
  {
    id: 'video-008', title: 'Shorts: Backstage del Concierto Acústico',
    description: '30 segundos de puro backstage con Los Ecos antes de su concierto acústico.',
    category: 'shorts', thumbnailPhotoId: 'photo-020', sourceType: 'upload', sourceUrl: '',
    duration: '0:42', status: 'published', isFeatured: false, fileSize: '98 MB', createdAt: '2026-06-04T19:00:00',
  },
  {
    id: 'video-009', title: 'Cobertura: Meet & Greet con oyentes',
    description: 'Cobertura completa del encuentro con nuestra comunidad.',
    category: 'eventos', thumbnailPhotoId: 'photo-014', sourceType: 'facebook', sourceUrl: 'https://facebook.com/watch?v=example2',
    duration: '28:15', status: 'published', isFeatured: false, fileSize: '890 MB', createdAt: '2026-03-09T11:00:00',
    derivedFromLiveId: 'live-010',
  },
  {
    id: 'video-010', title: 'Promo: Concurso "Tu canción al aire"',
    description: 'Spot del nuevo concurso donde los oyentes eligen la playlist.',
    category: 'promociones', thumbnailPhotoId: 'photo-016', sourceType: 'upload', sourceUrl: '',
    duration: '0:45', status: 'scheduled', isFeatured: false, fileSize: '210 MB', createdAt: '2026-06-08T08:00:00', scheduledAt: '2026-06-12T09:00:00',
  },
  {
    id: 'video-011', title: 'Fines de Semana - Especial Electrónica',
    description: 'Mariana te trae 3 horas de los mejores sets de música electrónica.',
    category: 'programas', thumbnailPhotoId: 'photo-011', sourceType: 'upload', sourceUrl: '',
    duration: '3:05:00', status: 'draft', isFeatured: false, fileSize: '4.2 GB', createdAt: '2026-06-07T23:00:00',
  },
  {
    id: 'video-012', title: 'Entrevista: DJ Phantom - Escena local',
    description: 'El productor revelación del año nos visita para hablar de su EP debut.',
    category: 'entrevistas', thumbnailPhotoId: 'photo-002', sourceType: 'youtube', sourceUrl: 'https://youtube.com/watch?v=example3',
    duration: '38:40', status: 'draft', isFeatured: false, fileSize: '1.1 GB', createdAt: '2026-06-06T21:30:00',
  },
  {
    id: 'video-013', title: 'Shorts: El blooper de Diego en vivo',
    description: 'El momento más divertido de la semana.',
    category: 'shorts', thumbnailPhotoId: 'photo-010', sourceType: 'upload', sourceUrl: '',
    duration: '0:35', status: 'published', isFeatured: true, fileSize: '72 MB', createdAt: '2026-06-05T22:15:00',
  },
  {
    id: 'video-014', title: 'Cobertura: Concierto Acústico Completo',
    description: 'Registro completo del concierto acústico de Los Ecos en nuestro estudio. 12 temas en versiones íntimas.',
    category: 'eventos', thumbnailPhotoId: 'photo-017', sourceType: 'youtube', sourceUrl: 'https://youtube.com/watch?v=example4',
    duration: '1:20:00', status: 'published', isFeatured: false, fileSize: '2.4 GB', createdAt: '2026-06-04T10:00:00',
    derivedFromLiveId: 'live-007',
  },
  {
    id: 'video-015', title: 'Promo: Nueva programación Julio 2026',
    description: 'Anuncio oficial de la nueva grilla. Nuevos programas, nuevos horarios.',
    category: 'promociones', thumbnailPhotoId: 'photo-023', sourceType: 'upload', sourceUrl: '',
    duration: '2:15', status: 'scheduled', isFeatured: false, fileSize: '520 MB', createdAt: '2026-06-08T09:00:00', scheduledAt: '2026-06-20T08:00:00',
  },
  {
    id: 'video-016', title: 'Tardes de Pop - Especial Año Nuevo',
    description: 'El último programa del año con los mejores momentos del 2025.',
    category: 'programas', thumbnailPhotoId: 'photo-001', sourceType: 'upload', sourceUrl: '',
    duration: '4:10:00', status: 'draft', isFeatured: false, fileSize: '5.8 GB', createdAt: '2026-06-01T00:30:00',
  },
  {
    id: 'video-017', title: 'Shorts: Top 5 opening lines de la semana',
    description: 'Las 5 mejores líneas de apertura de nuestros locutores esta semana.',
    category: 'shorts', thumbnailPhotoId: 'photo-009', sourceType: 'facebook', sourceUrl: 'https://facebook.com/watch?v=example3',
    duration: '1:12', status: 'published', isFeatured: false, fileSize: '185 MB', createdAt: '2026-06-04T17:00:00',
  },
  {
    id: 'video-018', title: 'Entrevista: Productores del Festival',
    description: 'Conversamos con el equipo que hizo posible el Festival Parque Central.',
    category: 'entrevistas', thumbnailPhotoId: 'photo-005', sourceType: 'upload', sourceUrl: '',
    duration: '52:30', status: 'scheduled', isFeatured: false, fileSize: '1.6 GB', createdAt: '2026-06-08T10:30:00', scheduledAt: '2026-06-15T18:00:00',
  },
  {
    id: 'video-019', title: 'Promo: Sponsors Summer Fest',
    description: 'Agradecimiento especial a todos los sponsors que hacen posible el Summer Fest.',
    category: 'promociones', thumbnailPhotoId: 'photo-015', sourceType: 'upload', sourceUrl: '',
    duration: '1:45', status: 'draft', isFeatured: false, fileSize: '410 MB', createdAt: '2026-06-07T12:00:00',
  },
  {
    id: 'video-020', title: 'Shorts: Tutorial - Cómo mandar un saludo',
    description: '¿Querés mandar un saludo al aire? En 45 segundos te explicamos cómo hacerlo.',
    category: 'shorts', thumbnailPhotoId: 'photo-003', sourceType: 'upload', sourceUrl: '',
    duration: '0:45', status: 'published', isFeatured: false, fileSize: '95 MB', createdAt: '2026-06-03T09:30:00',
  },
];

// ─── Posts Canónicos ───────────────────────────────────────

export const canonicalPosts: CanonicalPost[] = [
  {
    id: 'post-001', title: '¡CONCURSO SORPRESA! Ganá 2 Pases VIP para el Festival del Parque',
    text: 'Este viernes sorteamos 2 pases VIP para el Festival de Música del Parque Central. Solo tenés que compartir esta publicación y contarnos en los comentarios por qué querés ir.',
    postType: 'image', imagePhotoId: 'photo-ext-001', status: 'published', isFeatured: true,
    engagement: { likes: 847, comments: 156, shares: 203, reach: 12400 },
    isSponsored: false, createdAt: '2026-06-17T14:30:00', updatedAt: '2026-06-17T14:30:00',
    referencesEventId: 'event-001',
  },
  {
    id: 'post-002', title: 'Tardes de Pop con Carlos Mendoza — EN VIVO',
    text: 'Sintonizá ahora las Tardes de Pop. Estamos al aire hasta las 7 PM con la mejor música, saludos en vivo y las noticias del espectáculo.',
    postType: 'image', imagePhotoId: 'photo-ext-002', status: 'published', isFeatured: false,
    engagement: { likes: 423, comments: 89, shares: 67, reach: 8900 },
    isSponsored: false, createdAt: '2026-06-17T16:00:00', updatedAt: '2026-06-17T16:00:00',
    referencesProgramId: 'prog-003',
  },
  {
    id: 'post-003', title: '¿Cuál es tu canción favorita de la semana?',
    text: 'Queremos armar el Top 10 con tus votos. Elegí tu canción favorita y la ponemos al aire este sábado en el conteo semanal.',
    postType: 'poll', status: 'published', isFeatured: false,
    engagement: { likes: 312, comments: 201, shares: 89, reach: 7200 },
    pollData: { pollType: 'single', isClosed: false, options: [
      { id: 1, text: 'Eclipse Total — Los Ecos del Sol', votes: 1247 },
      { id: 2, text: 'Bailando Hasta el Amanecer — DJ Nexo', votes: 1023 },
      { id: 3, text: 'Recuerdos de Abril — Valentina Ruiz', votes: 812 },
      { id: 4, text: 'Fuego en la Ciudad — Banda Cobalto', votes: 598 },
      { id: 5, text: 'Olas del Pacífico — Mar Azul', votes: 489 },
    ]},
    isSponsored: false, createdAt: '2026-06-16T09:00:00', updatedAt: '2026-06-16T09:00:00',
  },
  {
    id: 'post-004', title: 'Reviví el concierto del Parque Central',
    text: 'Una noche increíble con más de 5,000 personas vibrando al ritmo de la mejor música en vivo. Mirá el resumen completo en 4K.',
    postType: 'video', imagePhotoId: 'photo-ext-003', videoId: 'video-003', status: 'published', isFeatured: true,
    engagement: { likes: 1534, comments: 312, shares: 478, reach: 22100 },
    isSponsored: false, createdAt: '2026-06-15T22:00:00', updatedAt: '2026-06-15T22:00:00',
    derivedFromVideoId: 'video-003', referencesEventId: 'event-001',
  },
  {
    id: 'post-005', title: 'Festival de Música en el Parque Central — 21 de Junio',
    text: 'Radio Nueva Actitud presenta el Festival de Música en el Parque Central. Bandas en vivo, food trucks, sorteos y mucha música.',
    postType: 'image', imagePhotoId: 'photo-ext-004', status: 'scheduled', isFeatured: true,
    engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
    isSponsored: true, sponsorName: 'Cerveza Imperial', createdAt: '2026-06-14T10:00:00', updatedAt: '2026-06-17T09:15:00',
    scheduledAt: '2026-06-21T16:00:00', referencesEventId: 'event-001',
  },
  {
    id: 'post-006', title: 'Nueva Programación de Verano 2026',
    text: 'Presentamos la nueva grilla de programación para este verano. Nuevos programas, nuevos horarios y más música que nunca.',
    postType: 'image', imagePhotoId: 'photo-ext-005', status: 'draft', isFeatured: false,
    engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
    isSponsored: false, createdAt: '2026-06-17T11:20:00', updatedAt: '2026-06-17T12:00:00',
  },
  {
    id: 'post-007', title: 'Entrevista Exclusiva: Los Ecos del Sol en la cabina',
    text: 'Este jueves recibimos a Los Ecos del Sol, la banda revelación del año. Van a tocar en acústico y responder preguntas de los oyentes.',
    postType: 'text', status: 'scheduled', isFeatured: false,
    engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
    isSponsored: false, createdAt: '2026-06-16T16:45:00', updatedAt: '2026-06-16T16:45:00',
    scheduledAt: '2026-06-19T18:00:00',
  },
  {
    id: 'post-008', title: 'Galería: Meet & Greet con los oyentes',
    text: 'Las mejores fotos del encuentro con nuestros oyentes el sábado pasado. Gracias a todos los que vinieron.',
    postType: 'image', imagePhotoId: 'photo-ext-006', status: 'published', isFeatured: false,
    engagement: { likes: 678, comments: 94, shares: 45, reach: 5600 },
    isSponsored: false, createdAt: '2026-06-14T19:00:00', updatedAt: '2026-06-14T19:00:00',
  },
  {
    id: 'post-009', title: 'Domingos de Clásicos — Especial Años 80',
    text: 'Este domingo nos vestimos de nostalgia. 4 horas continuas de los mejores clásicos de los 80.',
    postType: 'text', status: 'scheduled', isFeatured: false,
    engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
    isSponsored: false, createdAt: '2026-06-15T08:30:00', updatedAt: '2026-06-17T11:00:00',
    scheduledAt: '2026-06-21T10:00:00',
  },
  {
    id: 'post-010', title: 'Resumen: Transmisión Especial de Año Nuevo',
    text: 'Un resumen en video de nuestra transmisión especial de Año Nuevo. 12 horas de música, saludos desde toda California.',
    postType: 'video', imagePhotoId: 'photo-ext-007', videoId: 'video-016', status: 'draft', isFeatured: false,
    engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
    isSponsored: false, createdAt: '2026-06-10T14:00:00', updatedAt: '2026-06-17T10:30:00',
  },
  {
    id: 'post-011', title: '¡Ganá entradas para el Summer Fest 2026!',
    text: 'Respondé la trivia musical del día en los comentarios y participá por entradas dobles para el Summer Fest 2026.',
    postType: 'image', imagePhotoId: 'photo-ext-008', status: 'published', isFeatured: false,
    engagement: { likes: 523, comments: 312, shares: 167, reach: 18500 },
    isSponsored: true, sponsorName: 'Summer Fest CR', createdAt: '2026-06-16T08:15:00', updatedAt: '2026-06-16T12:00:00',
    referencesEventId: 'event-001',
  },
  {
    id: 'post-012', title: 'La Hora del Vinilo — Especial The Beatles',
    text: 'Una hora dedicada a la discografía de The Beatles en vinilo. Escuchamos los álbumes completos, anécdotas de grabación.',
    postType: 'image', imagePhotoId: 'photo-ext-009', status: 'scheduled', isFeatured: false,
    engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
    isSponsored: false, createdAt: '2026-06-17T13:00:00', updatedAt: '2026-06-17T13:00:00',
    scheduledAt: '2026-06-22T21:00:00',
  },
  {
    id: 'post-013', title: '¡Estrenamos página web completamente renovada!',
    text: 'Ahora podés escucharnos en vivo, ver la galería de fotos, chatear en la cabina y mucho más desde cualquier dispositivo.',
    postType: 'image', imagePhotoId: 'photo-ext-010', status: 'published', isFeatured: true,
    engagement: { likes: 983, comments: 145, shares: 234, reach: 15600 },
    isSponsored: false, createdAt: '2026-06-13T12:00:00', updatedAt: '2026-06-13T12:00:00',
  },
  {
    id: 'post-014', title: 'Detrás de cámara: Preparando el show de la noche',
    text: 'Así se prepara el equipo antes de cada transmisión. Revisando playlist, ajustando niveles y calentando la voz.',
    postType: 'image', imagePhotoId: 'photo-ext-011', status: 'published', isFeatured: false,
    engagement: { likes: 567, comments: 78, shares: 34, reach: 4800 },
    isSponsored: false, createdAt: '2026-06-15T20:30:00', updatedAt: '2026-06-15T20:30:00',
  },
  {
    id: 'post-015', title: 'Encuesta: ¿Qué género musical preferís para los viernes?',
    text: 'Los viernes son de fiesta y queremos saber qué te gusta escuchar. Votá por tu género favorito.',
    postType: 'poll', status: 'published', isFeatured: false,
    engagement: { likes: 245, comments: 178, shares: 56, reach: 6100 },
    pollData: { pollType: 'multiple', isClosed: false, options: [
      { id: 1, text: 'Rock en Español', votes: 456 },
      { id: 2, text: 'Pop Latino', votes: 389 },
      { id: 3, text: 'Electrónica / House', votes: 312 },
      { id: 4, text: 'Reggaetón', votes: 278 },
      { id: 5, text: 'Salsa / Tropical', votes: 198 },
      { id: 6, text: 'Cumbia', votes: 145 },
    ]},
    isSponsored: false, createdAt: '2026-06-14T10:00:00', updatedAt: '2026-06-14T10:00:00',
  },
  {
    id: 'post-016', title: 'Promo: 20% OFF en la tienda oficial de la radio',
    text: 'Por tiempo limitado, todo en la tienda oficial de Radio Nueva Actitud con 20% de descuento.',
    postType: 'image', imagePhotoId: 'photo-ext-012', status: 'published', isFeatured: false,
    engagement: { likes: 234, comments: 45, shares: 89, reach: 22300 },
    isSponsored: true, sponsorName: 'Tienda RNA', createdAt: '2026-06-15T15:00:00', updatedAt: '2026-06-15T15:00:00',
  },
  {
    id: 'post-017', title: 'Bienvenido 2026: Un mensaje de todo el equipo',
    text: 'Gracias por ser parte de esta familia. Este año viene cargado de sorpresas, nueva música, más concursos y mucho más.',
    postType: 'text', status: 'published', isFeatured: false,
    engagement: { likes: 1234, comments: 267, shares: 156, reach: 9800 },
    isSponsored: false, createdAt: '2026-06-12T08:00:00', updatedAt: '2026-06-12T08:00:00',
  },
  {
    id: 'post-018', title: 'Concurso: Diseñá el nuevo logo de la radio',
    text: 'Invitamos a todos los diseñadores y creativos a participar en el concurso del nuevo logo de Radio Nueva Actitud.',
    postType: 'image', imagePhotoId: 'photo-ext-013', status: 'published', isFeatured: false,
    engagement: { likes: 456, comments: 234, shares: 312, reach: 13400 },
    isSponsored: false, createdAt: '2026-06-11T10:00:00', updatedAt: '2026-06-11T10:00:00',
  },
  {
    id: 'post-019', title: '¿A quién querés entrevistar?',
    text: 'Estamos armando la agenda de entrevistas para julio y queremos saber a quién te gustaría escuchar en la radio.',
    postType: 'poll', status: 'draft', isFeatured: false,
    engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
    pollData: { pollType: 'single', isClosed: false, options: [
      { id: 1, text: 'Banda Nacional', votes: 0 },
      { id: 2, text: 'Artista Internacional', votes: 0 },
      { id: 3, text: 'DJ / Productor', votes: 0 },
      { id: 4, text: 'Influencer / Creador de Contenido', votes: 0 },
    ]},
    isSponsored: false, createdAt: '2026-06-18T09:30:00', updatedAt: '2026-06-18T09:30:00',
  },
  {
    id: 'post-020', title: '¡Último día para votar en los Premios Radio 2026!',
    text: 'Hoy cierran las votaciones para los Premios Radio 2026. Entrá a nuestro perfil, votá por tus favoritos y ayudanos a ganar.',
    postType: 'image', imagePhotoId: 'photo-ext-014', status: 'scheduled', isFeatured: false,
    engagement: { likes: 0, comments: 0, shares: 0, reach: 0 },
    isSponsored: false, createdAt: '2026-06-18T10:00:00', updatedAt: '2026-06-18T10:00:00',
    scheduledAt: '2026-06-20T12:00:00',
  },
];

// ─── Post-live Recordings ──────────────────────────────────

export const canonicalRecordings: CanonicalRecording[] = [
  {
    id: 'rec-001', liveId: 'live-006', title: 'Mañanas con Natalia - Edición Especial',
    description: 'Natalia arranca la mañana con buena onda, noticias, el clima y la mejor playlist.',
    hostDisplayName: 'Natalia Ruiz', programName: 'Mañanas con Natalia',
    date: '2026-06-03', duration: '3:15:00', thumbnailPhotoId: 'photo-009',
    platform: 'Facebook Live', audiencePeak: 847, audienceAvg: 623, totalViews: 2340,
    status: 'available', createdAt: '2026-06-03T10:30:00',
  },
  {
    id: 'rec-002', liveId: 'live-007', title: 'Concierto Acústico Íntimo - Los Ecos',
    description: 'Transmisión exclusiva del concierto acústico de Los Ecos desde nuestro estudio.',
    hostDisplayName: 'Carlos Mendoza', programName: 'Tardes de Pop',
    date: '2026-06-04', duration: '1:35:00', thumbnailPhotoId: 'photo-017',
    platform: 'YouTube Live', audiencePeak: 2103, audienceAvg: 1456, totalViews: 8200,
    status: 'available', createdAt: '2026-06-04T21:30:00',
  },
  {
    id: 'rec-003', liveId: 'live-010', title: 'Meet & Greet Virtual con Oyentes',
    description: 'Transmisión especial donde los oyentes se conectan por videollamada.',
    hostDisplayName: 'Natalia Ruiz + Diego Ramírez',
    date: '2026-05-30', duration: '1:10:00', thumbnailPhotoId: 'photo-014',
    platform: 'Facebook Live', audiencePeak: 534, audienceAvg: 389, totalViews: 1560,
    status: 'available', createdAt: '2026-05-30T18:30:00',
  },
  {
    id: 'rec-004', liveId: 'live-012', title: 'Especial Navideño - Brindis con la Comunidad',
    description: 'El último live del año. Balance, agradecimientos, saludos de los oyentes.',
    hostDisplayName: 'Todo el equipo',
    date: '2025-12-24', duration: '2:45:00', thumbnailPhotoId: 'photo-014',
    platform: 'YouTube Live', audiencePeak: 3201, audienceAvg: 2400, totalViews: 12100,
    status: 'available', createdAt: '2025-12-24T22:45:00',
  },
];

// ─── Eventos Canónicos ─────────────────────────────────────

export const canonicalEvents: CanonicalEvent[] = [
  {
    id: 'event-001', title: 'Summer Fest 2026: Los Ecos en Vivo',
    description: 'El festival de verano más esperado llega a la radio. Los Ecos se presentan en vivo desde nuestra cabina principal con un show exclusivo de 2 horas.',
    date: '2026-07-14', time: '18:00', location: 'Cabina Principal - Radio Nueva Actitud, Av. Siempre Viva 742, Ciudad de México',
    eventType: 'concert', status: 'published', modality: 'presencial', capacity: 200, category: 'music',
    imagePhotoId: 'photo-005', highlighted: true, participants: 847, interested: 2156, shares: 1243, followers: 389, stationId: 1,
    relatedPostIds: ['post-001', 'post-005', 'post-011'], relatedVideoIds: ['video-003', 'video-005'], relatedLiveIds: ['live-005'],
  },
  {
    id: 'event-002', title: 'Meet & Greet con Andrea Martínez',
    description: 'Conocé a Andrea Martínez, la voz de Despierta con Energía, en un encuentro exclusivo para oyentes.',
    date: '2026-07-15', time: '10:00', location: 'Estudio A - Radio Nueva Actitud',
    eventType: 'meet_greet', status: 'published', modality: 'presencial', capacity: 50, category: 'community',
    imagePhotoId: 'photo-012', highlighted: false, participants: 234, interested: 891, shares: 567, followers: 156, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-003', title: 'Transmisión Especial: 24 Horas de Música',
    description: 'Por primera vez, Nueva Actitud transmite durante 24 horas ininterrumpidas.',
    date: '2026-07-16', time: '06:00', location: 'Streaming - Todas las plataformas',
    eventType: 'special_broadcast', status: 'published', modality: 'online', capacity: 0, category: 'music',
    imagePhotoId: 'photo-022', highlighted: true, participants: 1562, interested: 4532, shares: 3201, followers: 712, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-004', title: 'Firma de Autógrafos: Carlos Rivera',
    description: 'Carlos Rivera, el conductor de Tardes de Pop, firma copias de su nuevo libro "Historias al Aire".',
    date: '2026-07-20', time: '16:00', location: 'Lobby Principal - Radio Nueva Actitud',
    eventType: 'autograph', status: 'published', modality: 'presencial', capacity: 80, category: 'cultural',
    imagePhotoId: 'photo-013', highlighted: false, participants: 189, interested: 544, shares: 432, followers: 98, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-005', title: 'Sesión Acústica: Nuevos Talentos',
    description: 'Tres artistas emergentes seleccionados por nuestra comunidad se presentan en una sesión acústica íntima.',
    date: '2026-07-21', time: '19:30', location: 'Estudio B + Streaming',
    eventType: 'acoustic_session', status: 'draft', modality: 'hibrido', capacity: 40, category: 'music',
    imagePhotoId: 'photo-017', highlighted: false, participants: 456, interested: 1230, shares: 890, followers: 201, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-006', title: 'Lanzamiento del Podcast "Voces Reales"',
    description: 'Estrenamos nuestro nuevo podcast de entrevistas a profundidad.',
    date: '2026-07-17', time: '17:00', location: 'Auditorio + YouTube Live',
    eventType: 'launch', status: 'published', modality: 'hibrido', capacity: 150, category: 'cultural',
    imagePhotoId: 'photo-004', highlighted: true, participants: 312, interested: 978, shares: 654, followers: 143, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-007', title: 'Concierto de Aniversario: 5 Años al Aire',
    description: 'Celebramos 5 años de Nueva Actitud con un concierto épico.',
    date: '2026-06-01', time: '20:00', location: 'Teatro Metropolitan - Ciudad de México',
    eventType: 'concert', status: 'ended', modality: 'presencial', capacity: 3500, category: 'music',
    imagePhotoId: 'photo-006', highlighted: false, participants: 3456, interested: 8201, shares: 8921, followers: 1543, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-008', title: 'Meet & Greet Virtual: Artistas del Mes',
    description: 'Encuentro virtual con los tres artistas más votados del mes.',
    date: '2026-05-15', time: '15:00', location: 'Online - Plataforma Zoom',
    eventType: 'meet_greet', status: 'ended', modality: 'online', capacity: 300, category: 'music',
    imagePhotoId: 'photo-014', highlighted: false, participants: 678, interested: 1890, shares: 1234, followers: 267, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: ['live-010'],
  },
  {
    id: 'event-009', title: 'Sesión de Fotos con la Comunidad',
    description: 'Juntamos a nuestros oyentes más fieles para una sesión de fotos grupal.',
    date: '2026-05-28', time: '11:00', location: 'Parque México - Ciudad de México',
    eventType: 'autograph', status: 'ended', modality: 'presencial', capacity: 150, category: 'community',
    imagePhotoId: 'photo-014', highlighted: false, participants: 234, interested: 612, shares: 567, followers: 89, stationId: 1,
    relatedPostIds: ['post-008'], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-010', title: 'Batalla de Bandas: La Final',
    description: 'Las 4 bandas finalistas de nuestro concurso se enfrentan en vivo.',
    date: '2026-07-19', time: '19:00', location: 'Estudio A - Radio Nueva Actitud',
    eventType: 'concert', status: 'draft', modality: 'presencial', capacity: 120, category: 'music',
    imagePhotoId: 'photo-008', highlighted: false, participants: 923, interested: 2456, shares: 1876, followers: 512, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-011', title: 'Taller de Locución para Jóvenes',
    description: '¿Siempre soñaste con estar frente al micrófono? Taller gratuito de iniciación a la locución radial.',
    date: '2026-07-18', time: '09:00', location: 'Sala de Capacitación - Radio Nueva Actitud',
    eventType: 'acoustic_session', status: 'published', modality: 'presencial', capacity: 30, category: 'educational',
    imagePhotoId: 'photo-004', highlighted: false, participants: 145, interested: 387, shares: 398, followers: 72, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
  {
    id: 'event-012', title: 'Noche de Karaoke: Cantá con Nosotros',
    description: 'La noche más divertida de la radio. Subí al escenario, cantá tu canción favorita.',
    date: '2026-06-14', time: '20:30', location: 'Cabina Principal - Radio Nueva Actitud',
    eventType: 'acoustic_session', status: 'cancelled', modality: 'presencial', capacity: 80, category: 'music',
    imagePhotoId: 'photo-016', highlighted: false, participants: 567, interested: 1340, shares: 1023, followers: 198, stationId: 1,
    relatedPostIds: [], relatedVideoIds: [], relatedLiveIds: [],
  },
];

// ─── Helpers ───────────────────────────────────────────────

export function getPhotoById(id: string): CanonicalPhoto | undefined {
  return canonicalPhotos.find((p) => p.id === id);
}

export function getExternalPhotoById(id: string): { id: string; imageUrl: string } | undefined {
  return canonicalExternalPhotos.find((p) => p.id === id);
}

export function getPhotoUrl(photoId: string): string {
  const internal = getPhotoById(photoId);
  if (internal) return internal.imageUrl;
  const external = getExternalPhotoById(photoId);
  if (external) return external.imageUrl;
  return '';
}

export function getVideoById(id: string): CanonicalVideo | undefined {
  return canonicalVideos.find((v) => v.id === id);
}

export function getLiveById(id: string): CanonicalLive | undefined {
  return canonicalLives.find((l) => l.id === id);
}

export function getPostById(id: string): CanonicalPost | undefined {
  return canonicalPosts.find((p) => p.id === id);
}

export function getEventById(id: string): CanonicalEvent | undefined {
  return canonicalEvents.find((e) => e.id === id);
}

// Content stats for Dashboard — derivadas del motor unificado
export function getContentStats() {
  const publishedPosts = canonicalPosts.filter((p) => p.status === 'published').length;
  const scheduledPosts = canonicalPosts.filter((p) => p.status === 'scheduled').length;
  const draftPosts = canonicalPosts.filter((p) => p.status === 'draft').length;
  const publishedVideos = canonicalVideos.filter((v) => v.status === 'published').length;
  const activeLives = canonicalLives.filter((l) => l.status === 'live').length;
  const scheduledLives = canonicalLives.filter((l) => l.status === 'scheduled').length;
  const endedLives = canonicalLives.filter((l) => l.status === 'ended').length;
  const upcomingEvents = canonicalEvents.filter((e) => e.status === 'published').length;
  const endedEvents = canonicalEvents.filter((e) => e.status === 'ended').length;

  return {
    // Content-derived stats
    publicaciones: canonicalPosts.length,
    fotosSubidas: canonicalPhotos.length,
    videosSubidos: canonicalVideos.length,
    lives: canonicalLives.length,
    livesActivos: activeLives,
    eventosProximos: upcomingEvents,
    // Operational stats (hardcoded — no vienen del motor de contenido)
    oyentesActuales: 1284,
    reproduccionesHoy: 45678,
    oyentesMes: 142310,
    mensajesCabina: 234,
    storageUsadoGB: 3.2,
    storageTotalGB: 10,
    seguidores: 24500,
    programasActivos: 8,
    ingresosEstimados: 24760,
    notificacionesDisponibles: 3,
  };
}